import { useMemo } from 'react';

import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ConfirmationDialog } from '../../../../components/common/dialog/ConfirmationDialog';

interface ArchiveConfirmationDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    type: 'single' | 'batch';
    className?: string;
    classCount?: number;
    confirming?: boolean;
    action?: 'archive' | 'unarchive';
}

const ArchiveDialogHint = styled(Typography)`
    padding-top: 8px;
    color: ${(props) => props.theme.colors.text.secondary};
`;

export const ArchiveConfirmationDialog = ({
    open,
    onClose,
    onConfirm,
    type,
    className,
    classCount = 0,
    confirming,
    action = 'archive'
}: ArchiveConfirmationDialogProps) => {
    const { t } = useTranslation('admin_settings');

    const isArchiveAction = action === 'archive';
    const isSingleType = type === 'single';

    const title = useMemo(() => {
        if (isSingleType) {
            return isArchiveAction
                ? t('classes.archive-class-confirm-title')
                : t('classes.unarchive-class-confirm-title');
        }
        return isArchiveAction
            ? t('classes.archive-classes-confirm-title')
            : t('classes.unarchive-classes-confirm-title');
    }, [isSingleType, isArchiveAction, t]);

    const message = useMemo(() => {
        if (isSingleType) {
            return isArchiveAction
                ? t('classes.archive-class-confirm-message', { className, interpolation: { escapeValue: false } })
                : t('classes.unarchive-class-confirm-message', { className, interpolation: { escapeValue: false } });
        }
        return isArchiveAction
            ? t('classes.archive-classes-confirm-message', { count: classCount })
            : t('classes.unarchive-classes-confirm-message', { count: classCount });
    }, [isSingleType, isArchiveAction, className, classCount, t]);

    const confirmLabel = isArchiveAction ? t('classes.archive') : t('classes.unarchive');

    return (
        <ConfirmationDialog
            open={open}
            onClose={onClose}
            onConfirm={onConfirm}
            title={title}
            confirmLabel={confirmLabel}
            confirming={confirming}
            aria-describedby="archive-dialog-message"
            role="alertdialog"
        >
            <Typography variant="body1" id="archive-dialog-message">
                {message}
            </Typography>
            {isArchiveAction && (
                <ArchiveDialogHint variant="body2" id="archive-dialog-hint">
                    {t('archive-class-hint')}
                </ArchiveDialogHint>
            )}
        </ConfirmationDialog>
    );
};
